import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DataTable, Table, TableHead, TableRow, TableHeader, TableBody, TableCell,
  TableSelectAll, TableSelectRow, TableContainer,
} from 'carbon-components-react';
import { InlineLoading, InlineNotification, Modal, NumberInput, Select, SelectItem, TextInput, Toggle } from '@carbon/react';
import moment from 'moment';
import Icon from '../../Icon/Icon';
import { ConfirmModal } from '../../ConfirmModal';
import {
  complianceApi, ComplianceJob, ComplianceJobInput, ComplianceJobStatus, ComplianceLogEntry, ComplianceSettings,
} from '../../../data/complianceApi';

type StatusFilter = 'All' | 'Recording' | 'Paused' | 'Error';
type JobDraft = ComplianceJobInput & { id?: string };

const activeStatuses: ComplianceJobStatus[] = ['starting', 'running', 'stopping'];
const pausedStatuses: ComplianceJobStatus[] = ['created', 'stopped', 'completed', 'interrupted'];
const headers = [
  { key: 'name', header: 'Channel' },
  { key: 'sourceName', header: 'Source' },
  { key: 'presetName', header: 'Preset' },
  { key: 'storageName', header: 'Storage' },
  { key: 'segmentDuration', header: 'Segment' },
  { key: 'status', header: 'Status' },
  { key: 'pid', header: 'PID' },
  { key: 'updatedAt', header: 'Last activity' },
];

const matchesFilter = (job: ComplianceJob, filter: StatusFilter): boolean => {
  if (filter === 'All') return true;
  if (filter === 'Recording') return activeStatuses.includes(job.status);
  if (filter === 'Paused') return pausedStatuses.includes(job.status);
  return job.status === 'failed';
};

const statusClass = (filter: StatusFilter): string => {
  if (filter === 'Recording') return 'progress-success';
  if (filter === 'Paused') return 'progress-warning';
  if (filter === 'Error') return 'progress-danger';
  return 'progress-default';
};

const statusColor = (status: ComplianceJobStatus): string => {
  if (status === 'failed') return '#da1e28';
  if (activeStatuses.includes(status)) return '#24a148';
  if (status === 'interrupted') return '#f1c21b';
  return '#8d8d8d';
};

const emptyDraft = (settings: ComplianceSettings): JobDraft => ({
  name: '',
  sourceId: settings.sources[0]?.id ?? '',
  presetId: settings.presets[0]?.id ?? '',
  storageId: settings.storageLocations[0]?.id ?? '',
  outputPrefix: '',
  segmentDurationMs: settings.defaultSegmentDurationMs,
  autoStart: true,
});

const ComplianceRecord: React.FC = () => {
  const [jobs, setJobs] = useState<ComplianceJob[]>([]);
  const [settings, setSettings] = useState<ComplianceSettings | null>(null);
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('All');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [draft, setDraft] = useState<JobDraft | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState<ComplianceLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadJobs = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const response = await complianceApi.getJobs();
      setJobs(response.items);
      setError('');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([complianceApi.getSettings(), complianceApi.getJobs()])
      .then(([nextSettings, response]) => {
        if (!active) return;
        setSettings(nextSettings);
        setJobs(response.items);
        setError('');
      })
      .catch((reason) => active && setError(reason instanceof Error ? reason.message : String(reason)))
      .finally(() => active && setLoading(false));
    const timer = window.setInterval(() => void loadJobs(), 3000);
    return () => { active = false; window.clearInterval(timer); };
  }, [loadJobs]);

  const selectedJobs = useMemo(() => jobs.filter((job) => selectedIds.includes(job.id)), [jobs, selectedIds]);
  const filteredJobs = useMemo(() => jobs.filter((job) => matchesFilter(job, filterStatus)), [jobs, filterStatus]);
  const rows = filteredJobs.map((job) => ({
    id: job.id,
    name: job.name,
    sourceName: job.sourceName,
    presetName: job.presetName,
    storageName: job.storageName,
    segmentDuration: `${job.segmentDurationMs / 1000}s`,
    status: job.status,
    pid: job.pid ?? '-',
    updatedAt: job.updatedAt,
  }));

  const counts: Record<StatusFilter, number> = {
    All: jobs.length,
    Recording: jobs.filter((job) => activeStatuses.includes(job.status)).length,
    Paused: jobs.filter((job) => pausedStatuses.includes(job.status)).length,
    Error: jobs.filter((job) => job.status === 'failed').length,
  };

  const openNewEditor = () => {
    if (!settings) return;
    setDraft(emptyDraft(settings));
    setEditorOpen(true);
  };

  const openEditEditor = () => {
    const job = selectedJobs[0];
    if (!job) return;
    setDraft({
      id: job.id, name: job.name, sourceId: job.sourceId, presetId: job.presetId, storageId: job.storageId,
      outputPrefix: job.outputPrefix, segmentDurationMs: job.segmentDurationMs,
    });
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const payload: ComplianceJobInput = {
        name: draft.name.trim(), sourceId: draft.sourceId, presetId: draft.presetId, storageId: draft.storageId,
        outputPrefix: draft.outputPrefix?.trim() || undefined,
        segmentDurationMs: draft.segmentDurationMs,
        autoStart: draft.id ? undefined : draft.autoStart,
      };
      if (draft.id) await complianceApi.updateJob(draft.id, payload);
      else await complianceApi.createJob(payload);
      setEditorOpen(false);
      setDraft(null);
      await loadJobs();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  };

  const runForSelected = async (operation: (id: string) => Promise<unknown>, ids: string[]) => {
    setBusy(true);
    try {
      await Promise.all(ids.map(operation));
      setSelectedIds([]);
      await loadJobs();
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : String(reason);
      await loadJobs();
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setDeleteModalOpen(false);
    await runForSelected(complianceApi.deleteJob, selectedIds);
  };

  const handlePause = async () => {
    setPauseModalOpen(false);
    await runForSelected(complianceApi.stopJob, selectedJobs.filter((job) => activeStatuses.includes(job.status)).map((job) => job.id));
  };

  const handleResume = async () => {
    setResumeModalOpen(false);
    await runForSelected(complianceApi.startJob, selectedJobs.filter((job) => !activeStatuses.includes(job.status)).map((job) => job.id));
  };

  const openLogs = async () => {
    const job = selectedJobs[0];
    if (!job) return;
    setLogsOpen(true);
    setLogs([]);
    try {
      setLogs((await complianceApi.getLogs(job.id)).items);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  };

  const isEditing = Boolean(draft?.id);
  const draftValid = Boolean(draft?.name.trim() && draft.sourceId && draft.presetId && draft.storageId && draft.segmentDurationMs >= 1000);
  const actions = [
    { key: 'new', title: 'New', icon: 'newFolder' as const, disabled: !settings || busy, onClick: openNewEditor },
    { key: 'edit', title: 'Edit', icon: 'edit' as const, disabled: selectedJobs.length !== 1 || activeStatuses.includes(selectedJobs[0]?.status), onClick: openEditEditor },
    { key: 'delete', title: 'Delete', icon: 'delete' as const, disabled: !selectedJobs.length || selectedJobs.some((job) => activeStatuses.includes(job.status)), onClick: () => setDeleteModalOpen(true) },
    { key: 'pause', title: 'Pause', icon: 'control_pause' as const, disabled: !selectedJobs.some((job) => activeStatuses.includes(job.status)), onClick: () => setPauseModalOpen(true) },
    { key: 'resume', title: 'Resume', icon: 'control_play' as const, disabled: !selectedJobs.some((job) => !activeStatuses.includes(job.status)), onClick: () => setResumeModalOpen(true) },
    { key: 'logs', title: 'Logs', icon: 'text' as const, disabled: selectedJobs.length !== 1, onClick: () => void openLogs() },
  ];

  return (
    <>
      {error && <InlineNotification kind="error" title="Compliance service error" subtitle={error} onCloseButtonClick={() => setError('')} lowContrast />}
      {(loading || busy) && <InlineLoading description={loading ? 'Loading compliance jobs…' : 'Updating job…'} />}
      <TableContainer>
        <DataTable
          rows={rows}
          headers={headers}
          render={({ rows: tableRows, headers: tableHeaders, getHeaderProps, getRowProps, getTableProps, getSelectionProps, selectedRows }) => {
            const currentSelection = selectedRows.map((row) => row.id);
            if (JSON.stringify(currentSelection) !== JSON.stringify(selectedIds)) setSelectedIds(currentSelection);
            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div className="status-filters">
                    {actions.map((action) => (
                      <div key={action.key} className={`action-box action-${action.key} ${action.disabled ? 'disabled' : ''}`} onClick={() => !action.disabled && action.onClick()}>
                        <Icon type={action.icon} color="white" size={16} />
                        <div style={{ fontSize: 13, marginTop: 2 }}>{action.title}</div>
                      </div>
                    ))}
                    <div style={{ width: 20 }} />
                    {(['All', 'Recording', 'Paused', 'Error'] as StatusFilter[]).map((status) => (
                      <div key={status} className={`status-box ${statusClass(status)} ${filterStatus === status ? 'active' : ''}`} style={{ borderWidth: filterStatus === status ? 3 : 1 }} onClick={() => setFilterStatus(status)}>
                        <div>{status}</div><div style={{ marginTop: 4 }}>{counts[status]}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <Table {...getTableProps()}>
                  <TableHead><TableRow><TableSelectAll {...getSelectionProps()} />
                    {tableHeaders.map((header) => <TableHeader key={header.key} {...getHeaderProps({ header })}>{header.header}</TableHeader>)}
                  </TableRow></TableHead>
                  <TableBody>
                    {tableRows.map((row) => (
                      <TableRow key={row.id} {...getRowProps({ row })}>
                        <TableSelectRow {...getSelectionProps({ row })} />
                        {row.cells.map((cell) => {
                          if (cell.info.header === 'status') return <TableCell key={cell.id}><span style={{ color: statusColor(cell.value) }}>{String(cell.value).toUpperCase()}</span></TableCell>;
                          if (cell.info.header === 'updatedAt') return <TableCell key={cell.id}>{moment(cell.value).format('DD.MM.YYYY HH:mm:ss')}</TableCell>;
                          return <TableCell key={cell.id}>{cell.value}</TableCell>;
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            );
          }}
        />
      </TableContainer>

      <Modal
        open={editorOpen}
        modalHeading={isEditing ? 'Edit compliance job' : 'New compliance job'}
        primaryButtonText={isEditing ? 'Save job' : 'Create job'}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={!draftValid || busy}
        onRequestClose={() => setEditorOpen(false)}
        onRequestSubmit={() => void handleSave()}
        preventCloseOnClickOutside
      >
        {draft && settings && <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <TextInput id="compliance-job-name" labelText="Job name" value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
          <Select id="compliance-job-source" labelText="Source" value={draft.sourceId} onChange={(event) => setDraft({ ...draft, sourceId: event.target.value })}>
            {settings.sources.map((item) => <SelectItem key={item.id} value={item.id} text={item.name} />)}
          </Select>
          <Select id="compliance-job-preset" labelText="Preset" value={draft.presetId} onChange={(event) => setDraft({ ...draft, presetId: event.target.value })}>
            {settings.presets.map((item) => <SelectItem key={item.id} value={item.id} text={item.fileName || item.name} />)}
          </Select>
          <Select id="compliance-job-storage" labelText="Storage" value={draft.storageId} onChange={(event) => setDraft({ ...draft, storageId: event.target.value })}>
            {settings.storageLocations.map((item) => <SelectItem key={item.id} value={item.id} text={item.name} />)}
          </Select>
          <TextInput id="compliance-job-prefix" labelText="Output prefix (optional)" value={draft.outputPrefix ?? ''} onChange={(event) => setDraft({ ...draft, outputPrefix: event.target.value })} />
          <NumberInput id="compliance-job-segment" label="Segment duration (ms)" min={1000} step={1000} value={draft.segmentDurationMs} onChange={(_, data) => setDraft({ ...draft, segmentDurationMs: Number(data.value) || 0 })} />
          {!isEditing && <Toggle id="compliance-job-autostart" labelText="Start recording immediately" toggled={draft.autoStart !== false} onToggle={(value) => setDraft({ ...draft, autoStart: value })} />}
        </div>}
      </Modal>

      <Modal open={logsOpen} modalHeading={`Job logs${selectedJobs[0] ? ` — ${selectedJobs[0].name}` : ''}`} passiveModal onRequestClose={() => setLogsOpen(false)} size="lg">
        <pre style={{ maxHeight: '55vh', overflow: 'auto', padding: 12, background: '#161616', color: '#f4f4f4', whiteSpace: 'pre-wrap' }}>
          {logs.length ? logs.map((entry) => `${moment(entry.timestamp).format('HH:mm:ss')} [${entry.stream}] ${entry.message}`).join('\n') : 'No logs for this job.'}
        </pre>
      </Modal>

      <ConfirmModal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onConfirm={() => void handleDelete()} title="Delete jobs" message={`Delete ${selectedIds.length} compliance job${selectedIds.length === 1 ? '' : 's'}? Recorded files will remain.`} type="danger" />
      <ConfirmModal open={pauseModalOpen} onClose={() => setPauseModalOpen(false)} onConfirm={() => void handlePause()} title="Pause recording" message="Stop the selected running recorder processes?" type="danger" />
      <ConfirmModal open={resumeModalOpen} onClose={() => setResumeModalOpen(false)} onConfirm={() => void handleResume()} title="Resume recording" message="Start the selected compliance jobs?" type="danger" />
    </>
  );
};

export default ComplianceRecord;
